import React from 'react';
import { CandidateReputationActions } from '@axiom/const';
import type { CandidateReputationDetails } from '@axiom/validation';
import {
  Button,
  CondensedHeader,
  DateUtil,
  Flashy,
  Gutter,
  Layout,
  LayoutItem,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalSection,
  ReadonlyTextarea,
  useApi,
  Well,
} from '@axiom/ui';

import { CandidateReputationDetailsApi } from '../../api/candidate-reputation-details-api';

const actionLabels: Record<string, string> = {
  [CandidateReputationActions.SHARED]: 'Sent to talent:',
  [CandidateReputationActions.SHARED_LIVE]: 'Talked to talent:',
  [CandidateReputationActions.DISPUTED]: 'Dispute email sent:',
  [CandidateReputationActions.DISMISSED]: 'Dismissed at:',
};

export type ReputationDetailsHistoryModalProps = {
  onClose: () => void;
  reputationId: CandidateReputationDetails['id'];
};

export const ReputationHistoryModal = ({
  onClose,
  reputationId,
}: ReputationDetailsHistoryModalProps) => {
  const [{ data: historyData }] = useApi(
    CandidateReputationDetailsApi.readReputationHistory({ reputationId })
  );

  const displayDate = (date: CandidateReputationDetails['actionTakenAt']) => {
    return `${DateUtil.displayDateLongYear(date)} at ${DateUtil.displayFullTime(date)}`;
  };

  return (
    <Modal name="WRITTEN_HISTORY_MODAL" size="confirm">
      <ModalHeader
        name="HISTORY_MODAL_HEADER"
        onClose={() => {
          onClose();
        }}
      >
        History
      </ModalHeader>
      <ModalSection>
        {Array.isArray(historyData.actionTakenHistory) &&
        historyData.actionTakenHistory.length > 0 ? (
          <Layout verticalGutter="16px" direction="vertical">
            {historyData.actionTakenHistory.map((data, idx, ary) => {
              return (
                <LayoutItem key={data.actionTakenAt}>
                  <Well background="light blue">
                    <Layout direction="horizontal" name="HISTORY_ROW">
                      <LayoutItem fluid>
                        <Flashy bold>{actionLabels[data.actionTaken]}</Flashy>
                      </LayoutItem>
                      <LayoutItem>{displayDate(data.actionTakenAt)}</LayoutItem>
                    </Layout>
                    {data.actionNotes ? (
                      <Gutter top="8px" name="HISTORY_NOTES">
                        <CondensedHeader>Notes:</CondensedHeader>
                        <Well background="light">
                          <ReadonlyTextarea
                            value={data.actionNotes}
                            formatting="modern"
                          />
                        </Well>
                      </Gutter>
                    ) : null}
                  </Well>
                </LayoutItem>
              );
            })}
          </Layout>
        ) : (
          <Layout direction="horizontal" name="NO_HISTORY_ROW">
            <LayoutItem fluid>No actions taken:</LayoutItem>
            <LayoutItem>--</LayoutItem>
          </Layout>
        )}
      </ModalSection>

      <ModalFooter>
        <Button
          pattern="primary"
          variation="outline"
          name="CANCEL_BUTTON"
          onClick={() => {
            onClose();
          }}
        >
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
};
